import React, { useState } from 'react';
import { Outlet, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { styled, createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import {
    Box, AppBar as MuiAppBar, Toolbar, IconButton, Drawer as MuiDrawer, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, CssBaseline,
    ListSubheader, Tooltip, Avatar
} from '@mui/material';

// Ícones
import {
    Menu as MenuIcon, Logout, Person, Settings, Dashboard as DashboardIcon,
    People as PeopleIcon, CorporateFare as CorporateFareIcon, Wallet as WalletIcon,
    BarChart as BarChartIcon, SupervisorAccount as SupervisorAccountIcon, ManageAccounts as ManageAccountsIcon
} from '@mui/icons-material';

// Importe suas imagens de logo
import logoImage from '../assets/logonav.png';
import useAuth from '../hooks/useAuth';

// --- IMPORTAÇÃO DA NOSSA CONFIGURAÇÃO DE URL ---
import { API_BASE_URL } from '../config'; 

const drawerWidth = 280;

// ============================================================================
// TEMA ESCURO GLOBAL PARA O LAYOUT
// ============================================================================
const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#315e9a' },
        background: {
            default: '#0f1218', // Fundo principal da tela inteira
            paper: '#1a1d24',   // Fundo da barra lateral e menus
        },
        divider: 'rgba(255, 255, 255, 0.05)'
    }
});

// ============================================================================
// ESTRUTURA DOS ITENS DE NAVEGAÇÃO
// ============================================================================
const menuItems = [
    {
        section: 'Principal',
        items: [
            { text: 'Dashboard', icon: <DashboardIcon />, path: '/inicial' },
            { text: 'Relatórios', icon: <BarChartIcon />, path: '/relatorios', roles: ['admin', 'superadmin'] },
        ]
    },
    {
        section: 'Gestão',
        items: [
            { text: 'Colaboradores', icon: <PeopleIcon />, path: '/colaboradores', roles: ['admin', 'superadmin'] },
            { text: 'Organização', icon: <CorporateFareIcon />, path: '/organizacao/gerenciar', roles: ['admin', 'superadmin'] },
            { text: 'Planos e Faturamento', icon: <WalletIcon />, path: '/planos', roles: ['admin', 'superadmin'] },
        ]
    },
    {
        section: 'Super Admin',
        roles: ['superadmin'], 
        items: [
            { text: 'Gerenciar Empresas', icon: <SupervisorAccountIcon />, path: '/empresas' },
            { text: 'Gerenciar Usuários', icon: <ManageAccountsIcon />, path: '/superadmin/usuarios' },
        ]
    }
];

// ============================================================================
// ESTILOS PARA O DRAWER (BARRA LATERAL)
// ============================================================================
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});

const AppBar = styled(MuiAppBar)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
}));

const PermanentDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && { ...openedMixin(theme), '& .MuiDrawer-paper': openedMixin(theme) }),
        ...(!open && { ...closedMixin(theme), '& .MuiDrawer-paper': closedMixin(theme) }),
    }),
);

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    minHeight: 48,
    padding: '10px 16px',
    borderRadius: '8px',
    margin: '4px 12px',
    color: theme.palette.text.secondary,
    '& .MuiListItemIcon-root': {
        minWidth: 0,
        justifyContent: 'center',
        color: 'inherit',
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
    },
    '&.Mui-selected': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
        color: theme.palette.primary.light,
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.25),
        }
    },
}));

// ============================================================================
// LAYOUT PRINCIPAL
// ============================================================================
const DashboardLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [anchorElProfile, setAnchorElProfile] = useState(null);

    const handleDrawerToggle = () => setDrawerOpen(!isDrawerOpen);
    const handleProfileMenuOpen = (event) => setAnchorElProfile(event.currentTarget);
    const handleProfileMenuClose = () => setAnchorElProfile(null);
    const handleLogout = () => {
        handleProfileMenuClose();
        auth.logout();
        navigate('/login');
    };
    
    let avatarCompletoUrl = '';
    if (auth.user?.avatarUrl) {
        const imagePath = `/uploads/${auth.user.avatarUrl.replace(/\\/g, '/')}`;
        avatarCompletoUrl = new URL(imagePath, API_BASE_URL).href;
    }

    const drawerContent = (
        <>
            <Toolbar />
            <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {menuItems.map((group, index) => {
                    if (group.roles && (!auth.user || !group.roles.includes(auth.user.role))) { return null; }
                    return (
                        <React.Fragment key={group.section}>
                            {!isDrawerOpen && index > 0 && <Divider sx={{ my: 1, mx: 2.5 }} />}
                            <List subheader={isDrawerOpen ? <ListSubheader sx={{ bgcolor: 'transparent' }}>{group.section}</ListSubheader> : null}>
                                {group.items.map((item) => {
                                    if (item.roles && (!auth.user || !item.roles.includes(auth.user.role))) { return null; }
                                    return (
                                        <Tooltip key={item.text} title={!isDrawerOpen ? item.text : ''} placement="right">
                                            <ListItem disablePadding>
                                                <StyledListItemButton
                                                    onClick={() => navigate(item.path)}
                                                    selected={location.pathname.startsWith(item.path)}
                                                    sx={{ justifyContent: isDrawerOpen ? 'initial' : 'center' }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 0, mr: isDrawerOpen ? 3 : 0, justifyContent: 'center' }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={item.text} sx={{ opacity: isDrawerOpen ? 1 : 0, transition: 'opacity 0.2s' }} />
                                                </StyledListItemButton>
                                            </ListItem>
                                        </Tooltip>
                                    );
                                })}
                            </List>
                        </React.Fragment>
                    );
                })}
            </Box>
            <Divider sx={!isDrawerOpen ? { my: 1, mx: 2.5 } : {}} />
            <List>
                <Tooltip title={!isDrawerOpen ? "Configurações" : ''} placement="right">
                    <ListItem disablePadding>
                        <StyledListItemButton onClick={() => navigate('/configuracoes')} selected={location.pathname === '/configuracoes'}
                            sx={{ justifyContent: isDrawerOpen ? 'initial' : 'center' }}
                        >
                            <ListItemIcon sx={{ minWidth: 0, mr: isDrawerOpen ? 3 : 'auto', justifyContent: 'center' }}><Settings /></ListItemIcon>
                            <ListItemText primary="Configurações" sx={{ opacity: isDrawerOpen ? 1 : 0, transition: 'opacity 0.2s' }} />
                        </StyledListItemButton>
                    </ListItem>
                </Tooltip>
            </List>
        </>
    );

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ display: 'flex' }}>
                <CssBaseline />
                <AppBar
                    position="fixed"
                    elevation={0}
                    sx={{
                        backgroundColor: '#050a30', // Mantido o azul escuro na barra superior
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                    }}
                >
                    <Toolbar>
                        <IconButton
                            aria-label="toggle drawer"
                            onClick={handleDrawerToggle}
                            edge="start"
                            sx={{ color: '#FFFFFF', marginRight: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        
                        <RouterLink to="/inicial">
                            <Box component="img" sx={{ height: { xs: 32, sm: 40 }, display: 'block' }} alt="Logo" src={logoImage} />
                        </RouterLink>

                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton onClick={handleProfileMenuOpen} size="small">
                            <Avatar
                                sx={{ width: 32, height: 32 }}
                                src={avatarCompletoUrl} 
                                alt={auth.user?.name ? auth.user.name[0].toUpperCase() : 'U'}
                            >
                                {auth.user?.name ? auth.user.name.split(' ').map(n=>n[0]).join('').toUpperCase() : 'U'}
                            </Avatar>
                        </IconButton>
                        <Menu anchorEl={anchorElProfile} open={Boolean(anchorElProfile)} onClose={handleProfileMenuClose}>
                            <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/perfil'); }}><ListItemIcon><Person fontSize="small" /></ListItemIcon>Meu Perfil</MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Sair</MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>

                <PermanentDrawer variant="permanent" open={isDrawerOpen}>
                    {drawerContent}
                </PermanentDrawer>

                {/* AQUI ESTAVA O PROBLEMA: bgcolor estava como '#F7F9FC' */}
                <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
                    <Toolbar />
                    <Outlet />
                </Box>
            </Box>
        </ThemeProvider>
    );
}

export default DashboardLayout;